<script setup lang="ts">
//#region imports
import { computed, nextTick, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { omit } from 'lodash-es';
import { useTableFilter, useTablePaginator, useTableSearch, useTableSort } from '@/composables/useTableLS';
import { usePermissions } from '@/composables/usePermissions';
import { useApi } from '@/api';
import { useUpdateUrl } from '@/composables/useUpdateUrl';
import { useLodash } from '@/setup/lodash';
import { usePopupManager } from '@/components/PopupManager.vue';
import { useDictList } from '@/composables/useDictList';
import { useTypedStore } from '@/store';
import type { UiTableNewField, UiTableNewFilter, UiTableNewSortEmit } from '@/types/table';
import type { GetDomainsManagementRegistrarAccountsResponse, GetDomainsManagementRegistrarAccountsData } from '@/types/openapi-ts';
import type { SettingsPageQuery } from '@/types/core';
import type UiPagination from '@/components/UIElements/UiPagination.vue';
//#endregion

//#region types
type DomainRegistrarAccountsQuery = SettingsPageQuery<GetDomainsManagementRegistrarAccountsData['query']>;
//#endregion

//#region const
const canBeUndefined = [
  'auto_ns_management',
];

const LS_PATH = 'DomainRegistrarAccounts';

const sizes = {
  1080: 20,
  1440: 30,
};

const fields: UiTableNewField<GetDomainsManagementRegistrarAccountsResponse['payload'][number]>[] = [
  {
    name: 'incrementing_id',
    sortable: 'custom',
    align: 'left',
    width: '60',
  },
  {
    name: 'name',
    label: 'account_name',
    sortable: 'custom',
    minWidth: '100',
  },
  {
    name: 'domain_registrar_name',
    sortable: 'custom',
    computedValue: (row) => row.domain_registrar_name || '—',
  },
  {
    name: 'platform_service_domains_count',
    align: 'right',
    sortable: 'custom',
    format: 'number',
  },
  {
    name: 'auto_ns_management',
    sortable: 'custom',
    computedValue(row) {
      return row.auto_ns_management ? 'Auto' : 'Manual';
    },
  },
  {
    name: 'created_at',
    sortable: 'custom',
    width: '120',
    format: 'date-time',
  },
];
//#endregion

//#region composables
const api = useApi();
const route = useRoute();
const store = useTypedStore();
const updateUrl = useUpdateUrl();
const { t } = useI18n();
const _ = useLodash();
const { hasPermission } = usePermissions();
const popupManager = usePopupManager();
const { searchQuery } = useTableSearch();
const { allAutoManualListBoolean } = useDictList();
const { limit, count, page, setPage } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});
const { sortProp, sortOrder, optionsSort } = useTableSort({
  initialOptions:{
    sort_column: 'incrementing_id',
    sort_dir: 'desc',
  },
  lsPath: LS_PATH,
  fields,
});
const { filterParams, setFilter } = useTableFilter({
  lsPath: LS_PATH,
});
//#endregion

//#region state
const tableData = ref<GetDomainsManagementRegistrarAccountsResponse['payload']>([]);
const isDataLoading = ref(false);
const isFirstLoading = ref(true);
//#endregion

//#region computed
const domainRegistrarProviders = computed(() => store.getters['misc/domainRegistrarProviders']);

const canEditDomainRegistrarAccount = computed(() => {
  return hasPermission('domain_registrar_accounts_edit');
});

const filtersList = computed(() => {
  return [
    {
      id: 'auto_ns_management',
      name: 'autoNsManagement',
      type: 'radio',
      params: {
        options: allAutoManualListBoolean.value,
      },
    },
    {
      id: 'domain_registrar_id',
      name: 'domainRegistrarId',
      type: 'select',
      params: {
        list: domainRegistrarProviders.value,
      },
    },
  ];
});
//#endregion

//#region methods
function initData() {
  const _urlData = updateUrl.getParseParams(route.hash);

  if (!_.isEmpty(_urlData)) {
    setPage(_urlData);
    setFilter(_urlData);
    delete _urlData.page;
    delete _urlData.limit;

    _.merge(optionsSort.value, _urlData);
  }
  nextTick(() => {
    fetchData();
  });
}

function _query() {
  return {
    ...optionsSort.value,
    limit: limit.value,
    search: searchQuery.value,
    offset: limit.value * page.value - limit.value,
  } as DomainRegistrarAccountsQuery;
}

function _completedFilterUrl() {
  const _dataFilters: DomainRegistrarAccountsQuery = omit(_query(), 'offset');
  _dataFilters.page = page.value;
  _dataFilters.filters = JSON.stringify(filterParams.value);
  if(Object.keys(filterParams.value).length <= 0) {
    delete _dataFilters.filters;
  }
  updateUrl.updateGetParams(_dataFilters);
}

async function fetchData(silent = false) {
  if (!silent && !isFirstLoading.value) {
    isDataLoading.value = true;
  }

  _completedFilterUrl();
  let options = _query();
  options = {
    ...options,
    ...filterParams.value,
  };

  try {
    const { data: { payload, misc } } = await api.getDomainRegistrarAccounts(options);

    count.value = misc!.count;
    tableData.value = payload;
  } finally {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  }

  if(!isFirstLoading.value) {
    isDataLoading.value = false;
  } else {
    isFirstLoading.value = false;
  }
}
function handleRefresh () {
  page.value = 1;
  fetchData();
}

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};

function handleDelete(row: GetDomainsManagementRegistrarAccountsResponse['payload'][number]) {
  const confirmPopup = popupManager.confirm.open({
    data: {
      title: t('domainManagement.buttons.delete'),
      message: t('domainManagement.messages.delete', { name: row.name}),
      bodyIcon: 'trash-alt',
      confirmIcon: 'trash-alt',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('domainManagement.buttons.delete'),
      confirmType: 'negative',
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        try {
          await api.deleteDomainRegistrarAccount(row.id);
          await fetchData();
        } catch (error) {
          window.console.error(error);
        }
        context.handleClose();
      },
    },
  });
}

async function openDomainRegistrarAccountPopup(id = null) {
  popupManager.domainRegistrarAccountPopup.open({
    data: {
      domainId: id,
    },
    listeners: {
      success: () => {
        fetchData(true);
      },
    },
  });
}

function getDomainRegistrarAccountFilter(data: UiTableNewFilter['filter']) {
  filterParams.value = data;
  handleRefresh();
}
//#endregion

//#region hooks
initData();
//#endregion
</script>

<template>
  <div class="domain-registrar-accounts">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('domainManagement.pageTitle.domainRegistrarAccounts') }}
      </template>
      <template v-slot:left-slot>
        <UiInput
          v-model.trim="searchQuery"
          :width="180"
          is-search
          @search="handleRefresh"
        />
        <DefaultFilter
          :model-value="filterParams"
          :filters-list="filtersList"
          :can-be-undefined="canBeUndefined"
          @submit="getDomainRegistrarAccountFilter"
        />
      </template>
      <template v-slot:right-slot>
        <UiPagination
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
      </template>
      <template v-slot:actions>
        <UiButton
          v-if="canEditDomainRegistrarAccount"
          filled
          icon="plus"
          @click="openDomainRegistrarAccountPopup()"
        >
          {{ $t('domainManagement.buttons.add_dns_hosting_account') }}
        </UiButton>
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      lazy-loading
      is-static
      i18n-path="domainManagement"
      @sort="handleSort"
    >
      <template
        v-if="canEditDomainRegistrarAccount"
        v-slot:append
      >
        <ElTableColumn
          align="center"
          width="76"
        >
          <template v-slot:header="scope">
            <div :key="scope.$index">
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>
          <template v-slot="scope">
            <ActionIcon
              icon="pen"
              :tooltip="$t('domainManagement.buttons.edit')"
              @click="openDomainRegistrarAccountPopup(scope.row.id)"
            />
            <ActionIcon
              icon="trash-alt"
              color="var(--text-negative)"
              :tooltip="$t('domainManagement.buttons.delete')"
              :disabled="scope.row.platform_service_domains_count > 0"
              @click="handleDelete(scope.row)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>
